import React, { useState, useContext, useRef, useEffect, useMemo } from 'react';
import { useProducts } from './ProductContext';
import { useAuth } from './AuthContext';
import {
  FACTORY_POS_SALE_KINDS,
  factoryPosProductsForKind,
} from './constants/factoryStockCategories';
import { apiJson } from './apiClient';
import { pharmacyExpiryStatus } from './constants/pharmacyStockCategories';
import { isAutomotiveBusiness, isBrickFactoryUser, isBarPubUser, isPharmacyUser } from './utils/businessType';
import { productUnitLabel } from './constants/productUnits';
import BarcodeScanField from './components/BarcodeScanField';
import { tenantMayAccessPage } from './utils/tenantModules';
import { SalesHistoryContext } from './SalesHistoryContext';
import {
  SUCCESS_THEN_REDIRECT_MS,
  waitUntilMinElapsed,
} from './utils/submitFeedback';
import './SalesForm.css';

const BASE_PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash (full)' },
  { value: 'lipa_number', label: 'Lipa number' },
  { value: 'credit', label: 'Credit / deni (pay later)' },
];

function saleErrorMessage(rawMessage, { automotiveMode }) {
  const msg = String(rawMessage || '').trim();
  if (!msg) return 'Could not record sale. Try again.';
  if (
    automotiveMode &&
    /bricks?|tiles?|cement|matofali|raw material/i.test(msg)
  ) {
    return 'This spare part cannot be sold. Receive stock or add opening stock with a selling price first.';
  }
  return msg;
}

function availableStock(product, { pharmacyMode }) {
  if (!product) return 0;
  if (pharmacyMode) {
    return Math.max(0, Math.floor(Number(product.sellableQuantity ?? product.quantity) || 0));
  }
  return Math.max(0, Math.floor(Number(product.quantity) || 0));
}

function paymentMethodsForUser(user) {
  if (!isBarPubUser(user)) return BASE_PAYMENT_METHODS;
  return [
    { value: 'cash', label: 'Cash' },
    { value: 'lipa_number', label: 'M-Pesa / Airtel / Tigo (Lipa)' },
    { value: 'credit', label: 'Tab / deni (pay later)' },
  ];
}

const SalesForm = ({ onNavigate }) => {
  const { user } = useAuth();
  const paymentMethods = paymentMethodsForUser(user);
  const { products, refreshProducts } = useProducts();
  const factoryMode = isBrickFactoryUser(user);
  const pharmacyMode = isPharmacyUser(user);
  const automotiveMode = isAutomotiveBusiness(user);
  const [factorySaleKind, setFactorySaleKind] = useState(FACTORY_POS_SALE_KINDS.bricks);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [barcodeScan, setBarcodeScan] = useState('');
  const [barcodeLoading, setBarcodeLoading] = useState(false);
  const [quantity, setQuantity] = useState('1');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [amountPaid, setAmountPaid] = useState('');
  const [lipaNumber, setLipaNumber] = useState('');
  const [deliverToSite, setDeliverToSite] = useState(true);
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [destination, setDestination] = useState('');
  const [phase, setPhase] = useState('idle');
  const [sale, setSale] = useState(null);
  const [deliveryCreated, setDeliveryCreated] = useState(null);
  const [debtCreated, setDebtCreated] = useState(null);
  const [formError, setFormError] = useState('');
  const { recordSale } = useContext(SalesHistoryContext);
  const redirectRef = useRef(null);

  useEffect(() => {
    return () => {
      if (redirectRef.current) clearTimeout(redirectRef.current);
    };
  }, []);

  useEffect(() => {
    if (factoryMode) {
      refreshProducts({ syncPos: true });
    }
  }, [factoryMode, refreshProducts]);

  const sellableProducts = useMemo(() => {
    const list = Array.isArray(products) ? products : [];
    if (factoryMode) {
      return factoryPosProductsForKind(list, factorySaleKind);
    }
    return list.filter((p) => {
      if (pharmacyMode) {
        const sellable = Number(p.sellableQuantity ?? p.quantity) || 0;
        return sellable > 0;
      }
      if (automotiveMode) {
        return (Number(p.quantity) || 0) > 0 && (Number(p.sellingPrice) || 0) > 0;
      }
      return typeof p.quantity === 'undefined' || Number(p.quantity) > 0;
    });
  }, [products, factoryMode, pharmacyMode, automotiveMode, factorySaleKind]);

  const factorySellingCement = factoryMode && factorySaleKind === FACTORY_POS_SALE_KINDS.cement;

  useEffect(() => {
    if (!factoryMode) return;
    setSelectedProduct('');
    setQuantity('1');
    if (factorySaleKind === FACTORY_POS_SALE_KINDS.cement) {
      setDeliverToSite(false);
    }
  }, [factoryMode, factorySaleKind]);

  const selectedRow = useMemo(
    () => products.find((p) => String(p.id) === String(selectedProduct)),
    [products, selectedProduct]
  );

  const maxStock = useMemo(
    () => availableStock(selectedRow, { pharmacyMode }),
    [selectedRow, pharmacyMode]
  );

  const quantityNum = Math.max(0, parseInt(String(quantity), 10) || 0);
  const quantityOverStock = Boolean(selectedRow && maxStock > 0 && quantityNum > maxStock);
  const quantityInvalid = quantityNum < 1;

  useEffect(() => {
    if (!selectedProduct || maxStock <= 0) return;
    setQuantity((prev) => {
      const q = parseInt(String(prev), 10);
      if (!Number.isFinite(q) || q < 1) return '1';
      if (q > maxStock) return String(maxStock);
      return prev;
    });
  }, [selectedProduct, maxStock]);

  const handleQuantityChange = (rawValue) => {
    const raw = String(rawValue ?? '');
    if (raw === '') {
      setQuantity('');
      return;
    }
    const digits = raw.replace(/\D/g, '');
    if (!digits) {
      setQuantity('');
      return;
    }
    let n = parseInt(digits, 10);
    if (!Number.isFinite(n) || n < 1) {
      setQuantity('1');
      return;
    }
    if (maxStock > 0 && n > maxStock) n = maxStock;
    setQuantity(String(n));
  };

  const lineTotal = useMemo(() => {
    if (!selectedRow) return 0;
    const price = Number(selectedRow.sellingPrice) || 0;
    const q = Math.max(1, parseInt(quantity, 10) || 1);
    return Math.round(price * q);
  }, [selectedRow, quantity]);

  const needsDebtor = useMemo(() => {
    const paid = Number(amountPaid) || 0;
    return paymentMethod === 'credit' || (lineTotal > 0 && paid < lineTotal);
  }, [paymentMethod, amountPaid, lineTotal]);

  const paidPreview = useMemo(() => {
    if (!lineTotal) return 0;
    if (paymentMethod === 'credit') return 0;
    return Math.round(Number(amountPaid) || 0);
  }, [lineTotal, paymentMethod, amountPaid]);

  const balancePreview = useMemo(() => Math.max(0, lineTotal - paidPreview), [lineTotal, paidPreview]);

  const canOpenDebtsPage = typeof onNavigate === 'function' && tenantMayAccessPage('debts-list', user);

  useEffect(() => {
    // Keep amountPaid in sync with total for non-credit sales.
    if (!lineTotal) return;
    if (paymentMethod === 'credit') {
      setAmountPaid('0');
      return;
    }
    if (amountPaid === '' || Number(amountPaid) > lineTotal) {
      setAmountPaid(String(lineTotal));
    }
  }, [paymentMethod, lineTotal]);

  const stockHint = useMemo(() => {
    if (!selectedRow) return '';
    if (pharmacyMode) {
      const sellable = Number(selectedRow.sellableQuantity ?? selectedRow.quantity) || 0;
      const total = Number(selectedRow.quantity) || 0;
      let hint = `${sellable} sellable`;
      if (total !== sellable) hint += ` (${total} total incl. expired)`;
      if (selectedRow.nearestExpiryDate) {
        hint += ` · FEFO batch exp. ${selectedRow.nearestExpiryDate}`;
      }
      return hint;
    }
    if (selectedRow.quantity !== undefined) {
      return `${Number(selectedRow.quantity) || 0} in stock`;
    }
    return 'Stock not tracked';
  }, [selectedRow, pharmacyMode]);

  const lookupBarcode = async (scannedCode) => {
    const code = String(scannedCode ?? barcodeScan).trim();
    if (!code || !pharmacyMode) return;
    setBarcodeLoading(true);
    setFormError('');
    try {
      const data = await apiJson(`/pharmacy/products/barcode/${encodeURIComponent(code)}`);
      const id = data.product?.id;
      if (id) {
        setSelectedProduct(String(id));
        setBarcodeScan('');
      }
    } catch (e) {
      setFormError(e.message || 'No product for this barcode.');
    } finally {
      setBarcodeLoading(false);
    }
  };

  const submitDisabled =
    phase !== 'idle' ||
    !selectedProduct ||
    quantityInvalid ||
    quantityOverStock ||
    maxStock <= 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (phase !== 'idle') return;
    if (redirectRef.current) {
      clearTimeout(redirectRef.current);
      redirectRef.current = null;
    }
    setFormError('');
    const product = products.find((p) => p.id === selectedProduct || String(p.id) === String(selectedProduct));
    if (!product) {
      setFormError('Select a product.');
      return;
    }
    const stock = availableStock(product, { pharmacyMode });
    const q = parseInt(String(quantity), 10);
    if (!Number.isFinite(q) || q < 1) {
      setFormError('Enter a valid quantity.');
      return;
    }
    if (q > stock) {
      setFormError(
        pharmacyMode
          ? `Not enough non-expired stock. Only ${stock} available.`
          : automotiveMode
            ? `Quantity exceeds stock. Only ${stock} spare part(s) available.`
            : `Quantity exceeds stock. Only ${stock} available.`
      );
      return;
    }

    if (factoryMode && deliverToSite) {
      if (!customerName.trim()) {
        setFormError('Enter customer name for site delivery.');
        return;
      }
      if (!destination.trim()) {
        setFormError('Enter delivery site / destination.');
        return;
      }
    }

    const paid = paymentMethod === 'credit' ? 0 : Math.round(Number(amountPaid) || 0);
    const total = lineTotal;
    if (total <= 0) {
      setFormError(
        automotiveMode
          ? 'Set a selling price on this spare part first (opening stock or receive).'
          : 'Set a selling price on this product first.'
      );
      return;
    }
    if (paid < 0 || paid > total) {
      setFormError('Amount paid must be between 0 and the sale total.');
      return;
    }
    const hasBalance = paid < total;
    if (hasBalance && !customerName.trim() && !(factoryMode && deliverToSite)) {
      setFormError('Enter customer name — they owe a balance (deni).');
      return;
    }
    if (hasBalance && factoryMode && deliverToSite && !customerName.trim()) {
      setFormError('Enter customer name for delivery and debt tracking.');
      return;
    }

    let paymentLabel = 'Cash';
    if (paymentMethod === 'lipa_number') {
      const ref = lipaNumber.trim();
      if (!ref) {
        setFormError('Enter the Lipa number (till, phone, or reference you used).');
        return;
      }
      paymentLabel = `Lipa number: ${ref}`.slice(0, 80);
    } else if (paymentMethod === 'credit') {
      paymentLabel = 'Credit / deni';
    } else if (hasBalance) {
      paymentLabel = `Partial: TZS ${paid.toLocaleString()} of ${total.toLocaleString()}`;
    }

    const debtorName = customerName.trim() || null;
    const debtorPhone = customerPhone.trim() || null;

    setPhase('loading');
    const started = Date.now();
    try {
      const result = await recordSale({
        productId: Number(product.id),
        quantity: q,
        paymentMethod: paymentMethod === 'credit' ? 'credit' : paymentMethod,
        paymentLabel,
        amountPaid: paid,
        deliverToSite: factoryMode && deliverToSite,
        customerName: debtorName || (factoryMode && deliverToSite ? customerName : null),
        customerPhone: debtorPhone || (factoryMode && deliverToSite ? customerPhone : null),
        destination: factoryMode && deliverToSite ? destination : null,
      });
      await refreshProducts();
      await waitUntilMinElapsed(started);
      setSale(result?.sale ?? result);
      setDeliveryCreated(result?.delivery ?? null);
      setDebtCreated(result?.debt ?? null);
      setSelectedProduct('');
      setQuantity('1');
      setLipaNumber('');
      setCustomerName('');
      setCustomerPhone('');
      setDestination('');
      setPhase('success');
      redirectRef.current = setTimeout(() => {
        redirectRef.current = null;
        setPhase('idle');
        setSale(null);
        setDeliveryCreated(null);
        setDebtCreated(null);
        if (typeof onNavigate === 'function') {
          if (result?.debt) onNavigate('debts-list');
          else onNavigate(result?.delivery ? 'brick-deliveries' : 'pos-sale-history');
        }
      }, SUCCESS_THEN_REDIRECT_MS);
    } catch (err) {
      setFormError(saleErrorMessage(err.body?.message || err?.message, { automotiveMode }));
      setPhase('idle');
    }
  };

  const busy = phase === 'loading' || phase === 'success';

  return (
    <div className="pos-sale-page page-bleed">
      <header className="pos-sale-header">
        <h1 className="pos-sale-title">
          {factoryMode
            ? factorySellingCement
              ? 'Sell cement'
              : 'Sell ready bricks'
            : pharmacyMode
              ? 'Pharmacy POS (FEFO)'
              : 'Record sale'}
        </h1>
        <p className="pos-sale-lede">
          {pharmacyMode ? (
            <>
              Scan <strong>barcode</strong> or select medicine. System sells <strong>oldest expiry first (FEFO)</strong> and
              blocks sales if only expired stock remains.
            </>
          ) : factoryMode ? (
            <>
              Chagua <strong>Matofali</strong> au <strong>Cement</strong> — orodha inaonyesha bidhaa za aina hiyo tu. Mauzo ya
              matofali kwenye tovuti yanaonekana chini ya <strong>Deliveries</strong>. Deni linaandikwa ikiwa wamelipa sehemu tu.
            </>
          ) : (
            <>
              Enter <strong>how much they pay now</strong>; anything left is <strong>deni</strong> and is saved automatically. Open{' '}
              <strong>Customer debts</strong> from the link below to collect later.
            </>
          )}
        </p>
      </header>

      <div className="pos-sale-card">
        <form className="pos-sale-form" onSubmit={handleSubmit} noValidate>
          <div className="pos-sale-grid">
            {pharmacyMode ? (
              <div className="pos-sale-field pos-sale-field--full">
                <BarcodeScanField
                  value={barcodeScan}
                  onChange={setBarcodeScan}
                  onScan={(code) => void lookupBarcode(code)}
                  loading={barcodeLoading}
                  disabled={busy}
                  autoFocus
                  label="Scan barcode"
                  inputClassName="pos-sale-input"
                  id="pos-barcode-scan"
                />
              </div>
            ) : null}
            {factoryMode ? (
              <div className="pos-sale-field pos-sale-field--full">
                <span className="pos-sale-label">Unauza nini?</span>
                <div className="pos-sale-kind-toggle" role="group" aria-label="Sale type">
                  <button
                    type="button"
                    className={`pos-sale-kind-btn${
                      factorySaleKind === FACTORY_POS_SALE_KINDS.bricks ? ' pos-sale-kind-btn--active' : ''
                    }`}
                    onClick={() => setFactorySaleKind(FACTORY_POS_SALE_KINDS.bricks)}
                    disabled={busy}
                  >
                    Matofali
                  </button>
                  <button
                    type="button"
                    className={`pos-sale-kind-btn${
                      factorySaleKind === FACTORY_POS_SALE_KINDS.cement ? ' pos-sale-kind-btn--active' : ''
                    }`}
                    onClick={() => setFactorySaleKind(FACTORY_POS_SALE_KINDS.cement)}
                    disabled={busy}
                  >
                    Cement
                  </button>
                </div>
              </div>
            ) : null}
            <label className="pos-sale-field pos-sale-field--full">
              <span className="pos-sale-label">
                {factoryMode
                  ? factorySellingCement
                    ? 'Cement (stock)'
                    : 'Brick type (ready stock)'
                  : 'Product'}
              </span>
              <select
                className="pos-sale-input"
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
                required
                disabled={busy}
              >
                <option value="">Select a product…</option>
                {sellableProducts.length === 0 && (
                  <option value="" disabled>
                    {factoryMode
                      ? factorySellingCement
                        ? 'No cement in stock — set selling price on cement product first'
                        : 'No ready bricks in stock — record production first'
                      : automotiveMode
                        ? 'No spare parts ready to sell — add opening stock or receive purchase first'
                        : 'No products in stock'}
                  </option>
                )}
                {sellableProducts.map((p) => {
                  const q = pharmacyMode
                    ? Number(p.sellableQuantity ?? p.quantity) || 0
                    : p.quantity !== undefined
                      ? Number(p.quantity) || 0
                      : null;
                  const price = Number(p.sellingPrice) || 0;
                  const u = productUnitLabel(p.unit);
                  const exp =
                    pharmacyMode && p.nearestExpiryDate ? ` · exp ${p.nearestExpiryDate}` : '';
                  const suffix = q != null ? ` — ${q} ${u}${exp}` : '';
                  const priceSuffix =
                    price > 0 ? ` @ Tsh ${price.toLocaleString()}` : ' — set price in catalog';
                  return (
                    <option key={p.id} value={p.id}>
                      {p.name}
                      {suffix}
                      {priceSuffix}
                    </option>
                  );
                })}
              </select>
              {stockHint && <span className="pos-sale-hint">{stockHint}</span>}
            </label>

            <label className="pos-sale-field">
              <span className="pos-sale-label">
                Quantity
                {selectedRow?.unit
                  ? ` (${productUnitLabel(selectedRow.unit)})`
                  : factorySellingCement
                    ? ' (bags)'
                    : ''}
              </span>
              <input
                className="pos-sale-input pos-sale-input--qty"
                type="number"
                min={1}
                max={maxStock > 0 ? maxStock : undefined}
                value={quantity}
                onChange={(e) => handleQuantityChange(e.target.value)}
                onBlur={() => {
                  if (!quantity || quantityNum < 1) setQuantity('1');
                }}
                required
                disabled={busy || maxStock <= 0}
              />
              {maxStock > 0 ? (
                <span className="pos-sale-hint">Max: {maxStock} in stock</span>
              ) : null}
              {quantityOverStock ? (
                <span className="pos-sale-hint pos-sale-hint--error" role="alert">
                  Quantity cannot exceed stock ({maxStock} available).
                </span>
              ) : null}
            </label>

            <label className="pos-sale-field">
              <span className="pos-sale-label">Payment</span>
              <select
                className="pos-sale-input"
                value={paymentMethod}
                onChange={(e) => {
                  const v = e.target.value;
                  setPaymentMethod(v);
                  if (v === 'cash') setLipaNumber('');
                  if (v === 'credit') setAmountPaid('0');
                  else if (lineTotal > 0) setAmountPaid(String(lineTotal));
                }}
                required
                disabled={busy}
              >
                {paymentMethods.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </label>

            {lineTotal > 0 && (
              <label className="pos-sale-field">
                <span className="pos-sale-label">Amount paid now (TZS)</span>
                <input
                  className="pos-sale-input"
                  type="number"
                  min={0}
                  max={lineTotal}
                  value={amountPaid}
                  onChange={(e) => setAmountPaid(e.target.value)}
                  disabled={busy || paymentMethod === 'credit'}
                  required
                />
                <span className="pos-sale-hint">Change if they pay only part now; the summary below updates.</span>
              </label>
            )}

            {lineTotal > 0 && (
              <div className="pos-sale-money pos-sale-field--full" aria-live="polite">
                <div className="pos-sale-money__title">Sale total &amp; deni</div>
                <div className="pos-sale-money__grid">
                  <div className="pos-sale-money__cell">
                    <span className="pos-sale-money__label">Sale total</span>
                    <strong className="pos-sale-money__value">TZS {lineTotal.toLocaleString()}</strong>
                  </div>
                  <div className="pos-sale-money__cell">
                    <span className="pos-sale-money__label">Paid now</span>
                    <strong className="pos-sale-money__value">TZS {paidPreview.toLocaleString()}</strong>
                  </div>
                  <div
                    className={`pos-sale-money__cell pos-sale-money__cell--balance${
                      balancePreview > 0 ? ' pos-sale-money__cell--due' : ' pos-sale-money__cell--clear'
                    }`}
                  >
                    <span className="pos-sale-money__label">Balance (deni)</span>
                    <strong className="pos-sale-money__value">TZS {balancePreview.toLocaleString()}</strong>
                    <p className="pos-sale-money__hint">
                      {balancePreview > 0
                        ? 'This amount will be recorded as customer debt when you submit.'
                        : 'No balance — customer is paying the full sale total.'}
                    </p>
                  </div>
                </div>
                {canOpenDebtsPage && (
                  <button
                    type="button"
                    className="pos-sale-money__link"
                    onClick={() => onNavigate('debts-list')}
                    disabled={busy}
                  >
                    Open Customer debts →
                  </button>
                )}
              </div>
            )}

            {(needsDebtor && !(factoryMode && deliverToSite)) && (
              <>
                <label className="pos-sale-field pos-sale-field--full">
                  <span className="pos-sale-label">Customer name (debtor) *</span>
                  <input
                    className="pos-sale-input"
                    required
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    disabled={busy}
                  />
                </label>
                <label className="pos-sale-field">
                  <span className="pos-sale-label">Customer phone</span>
                  <input
                    className="pos-sale-input"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    disabled={busy}
                  />
                </label>
              </>
            )}

            {paymentMethod === 'lipa_number' && (
              <label className="pos-sale-field pos-sale-field--full">
                <span className="pos-sale-label">Lipa number / reference</span>
                <input
                  className="pos-sale-input"
                  type="text"
                  autoComplete="off"
                  placeholder="e.g. till no. or phone used for payment"
                  value={lipaNumber}
                  onChange={(e) => setLipaNumber(e.target.value)}
                  disabled={busy}
                  maxLength={64}
                />
                <span className="pos-sale-hint">This is stored on the sale receipt line for your records.</span>
              </label>
            )}

            {factoryMode && !factorySellingCement ? (
              <div className="pos-sale-fulfillment pos-sale-field--full">
                <span className="pos-sale-label">Fulfillment</span>
                <label className="pos-sale-check">
                  <input
                    type="checkbox"
                    checked={deliverToSite}
                    onChange={(e) => setDeliverToSite(e.target.checked)}
                    disabled={busy}
                  />
                  Deliver to customer site (shows in Deliveries)
                </label>
                {!deliverToSite ? (
                  <p className="pos-sale-hint">Customer collects bricks at the yard — no delivery trip.</p>
                ) : (
                  <div className="pos-sale-delivery-fields">
                    <label className="pos-sale-field pos-sale-field--full">
                      <span className="pos-sale-label">Customer name</span>
                      <input
                        className="pos-sale-input"
                        required
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        disabled={busy}
                        placeholder="Who ordered?"
                      />
                    </label>
                    <label className="pos-sale-field">
                      <span className="pos-sale-label">Phone</span>
                      <input
                        className="pos-sale-input"
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        disabled={busy}
                      />
                    </label>
                    <label className="pos-sale-field pos-sale-field--full">
                      <span className="pos-sale-label">Site / destination</span>
                      <input
                        className="pos-sale-input"
                        required
                        value={destination}
                        onChange={(e) => setDestination(e.target.value)}
                        disabled={busy}
                        placeholder="Construction site address or area"
                      />
                    </label>
                  </div>
                )}
              </div>
            ) : null}
          </div>

          {formError && (
            <div className="pos-sale-alert pos-sale-alert--error" role="alert">
              {formError}
            </div>
          )}
          {phase === 'success' && sale && (
            <div className="pos-sale-alert pos-sale-alert--success" role="status">
              Sale saved.
              {debtCreated
                ? ` Deni TZS ${Number(debtCreated.balanceAmount || 0).toLocaleString()} saved — opening Customer debts…`
                : deliveryCreated
                  ? ` Delivery queued for ${deliveryCreated.customerName} → ${deliveryCreated.destination}. Opening deliveries…`
                  : ' Redirecting to history…'}
            </div>
          )}

          <div className="pos-sale-actions">
            <button type="submit" className="pos-sale-submit" disabled={submitDisabled || busy}>
              {phase === 'loading' ? (
                <>
                  <span className="pos-sale-spinner" aria-hidden />
                  Saving…
                </>
              ) : phase === 'success' ? (
                'Redirecting…'
              ) : (
                'Submit sale'
              )}
            </button>
            {typeof onNavigate === 'function' && (
              <button type="button" className="pos-sale-secondary" onClick={() => onNavigate('overview')} disabled={busy}>
                Cancel
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default SalesForm;
